// Specific-output wizard entry ("Build landing pages", "Create a content
// strategy", ...): pick a project, see what that deliverable still needs,
// and jump into the wizard at the first missing step. This is entry_mode
// = specific_output made real — the prerequisite chain has lived in the
// schema since Phase 0; the states here are computed from data the API
// already returns (no new backend).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import PrereqChecklist from '../../components/PrereqChecklist';
import {
  BRAND, FONTS, HUES, INK, NEUTRAL, RADII,
  buttonLink, buttonPrimary, pageShell,
} from '../../utils/wizardTheme';
import { railName } from '../../utils/ciqRail';
import { CARDS } from './ContentIQHome';

const API = process.env.REACT_APP_API_URL;

// One definition per lane (strategy reset R1, 2026-10-15: the former
// matrix/scripts standalone deliverables are retired — personas live inside
// strategy work, scripts are a section of the strategy document). `needs`
// lists prereq keys in spine order; `wizardSection` is the project-rail
// section the wizard opens on once everything is satisfied, and `readyLabel`
// names that move.
export const OUTPUT_DEFS = {
  strategy: {
    title: 'Create a content strategy', lane: 'Content strategy',
    noun: 'a content strategy', hueKey: 'strategy', goal: 'GOAL: CONTENT STRATEGY',
    blurb: 'The strategy selects from the client\u2019s assets, guided by the research. Scripts ride inside it where the product includes them.',
    needs: ['research', 'personas', 'assets', 'strategy'],
    wizardSection: 'strategy', readyLabel: 'Open the strategy →',
    setupBlurb: 'add the client\u2019s assets and questions — then two builds (research and the buyer map, then the strategy) produce the strategy for your review.',
  },
  lp: {
    title: 'Write landing page copy', lane: 'Landing page copy',
    noun: 'landing-page copy', hueKey: 'lp', goal: 'GOAL: LANDING PAGE COPY',
    blurb: 'Strategy first, then each landing page\u2019s exact copy. Download the copy, or send it on to the build team when you\u2019re ready.',
    needs: ['research', 'personas', 'assets', 'strategy'],
    wizardSection: 'deploy', readyLabel: 'Continue to the page copy →',
    setupBlurb: 'add the client\u2019s assets and questions — then the strategy is built, and each landing page\u2019s copy is written from it for your review.',
  },
  // Lane 3 (Edward, 2026-10-22): the copy (or a finished strategy) was
  // written outside ContentIQ and all that's wanted is the built pages — no
  // research, no strategy. Rides the client-copy route: every asset carries
  // its page copy, assembled verbatim, then straight to the build team.
  pagebuild: {
    title: 'Build landing pages from your own copy', lane: 'Landing page build',
    noun: 'a landing-page build', hueKey: 'lp', goal: 'GOAL: LANDING PAGE BUILD',
    blurb: 'You already have the copy from outside ContentIQ. Add the client\u2019s assets, bring in each page\u2019s copy, and send the package to the build team — no research or strategy.',
    needs: ['assets', 'copy'],
    wizardSection: 'deploy', readyLabel: 'Continue to Page build →',
    setupBlurb: 'add the client\u2019s assets and each page\u2019s copy — the pages are assembled from that copy word for word and go to the build team. Nothing is researched or written.',
  },
};

// Which project-rail section resolves each unmet prerequisite (the
// wizard's ?section= keys), and that step's name on the rail. Research
// starts from Company & campaign.
const NEED_SECTION = { research: 'context', assets: 'assets', personas: 'personas', strategy: 'strategy', copy: 'assets' };
const NEED_STEP_NAME = Object.fromEntries(
  Object.entries(NEED_SECTION).map(([need, section]) => [need, railName(section)]));
// Labels match the setup wizard's own step vocabulary (F3 sweep, 2026-08-26).
const NEED_LABELS = {
  research: 'Company & campaign context',
  personas: 'Buyer map',
  assets: 'Client assets',
  strategy: 'Content strategy',
  copy: 'The client\u2019s copy for every page',
};

// Compute each prerequisite's state from already-available API data.
export function computePrereqs(def, data) {
  const { context, mapRows, assets, strategy } = data;
  const promotableAssets = (assets || []).filter(a => a.is_promotable && a.kind !== 'brand'
    && a.kind !== 'build_image' && !a.flow_role);
  const promotable = promotableAssets.length;
  const withCopy = promotableAssets.filter(a => a.has_client_copy).length;
  const states = {
    research: context?.research_profile
      ? { state: 'done', detail: `research run · ${(context.research_profile.sources_used || []).length} source${(context.research_profile.sources_used || []).length === 1 ? '' : 's'}` }
      : { state: 'todo', detail: 'not run yet — it starts from Company & campaign' },
    personas: (mapRows || []).length > 0
      ? { state: 'done', detail: `${mapRows.length} row${mapRows.length === 1 ? '' : 's'} in the buyer map` }
      : { state: 'todo', detail: 'no buyer-map rows yet — Buyer map step' },
    assets: promotable > 0
      ? { state: 'done', detail: `${promotable} client asset${promotable === 1 ? '' : 's'}` }
      : { state: 'todo', detail: 'no client assets yet — add them on the Client assets step' },
    copy: promotable > 0 && withCopy === promotable
      ? { state: 'done', detail: `every asset carries the client\u2019s copy (${promotable})` }
      : { state: 'todo', detail: promotable
          ? `${promotable - withCopy} of ${promotable} assets still need the client\u2019s copy — Client assets step`
          : 'add the assets first, then each one\u2019s copy — Client assets step' },
    strategy: (strategy?.items || []).length > 0
      ? { state: 'done', detail: `${strategy.items.length} recommendations · ${((strategy.page_plan || {}).pages || []).length} planned page${(((strategy.page_plan || {}).pages || []).length === 1) ? '' : 's'}` }
      : { state: 'todo', detail: 'not generated yet — Content strategy step' },
  };
  const items = def.needs.map(key => ({ key, label: NEED_LABELS[key], ...states[key] }));
  const firstMissing = items.find(it => it.state !== 'done');
  return { items, firstMissing };
}

export default function ContentIQStart() {
  const { output } = useParams();
  const navigate = useNavigate();
  const def = OUTPUT_DEFS[output];

  // A retired output key (matrix/scripts bookmarks) lands on the Home's
  // three lanes instead of a broken page.
  useEffect(() => { if (!def) navigate('/contentiq', { replace: true }); }, [def, navigate]);

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Jumping into a deliverable most often means STARTING a project
  // (Edward, 2026-08-24) — new is the default, existing is the secondary path.
  const [mode, setMode] = useState('new');

  useEffect(() => {
    axios.get(`${API}/contentiq/projects/`).then(r => {
      setProjects(r.data);
      if (r.data.length > 0) setProjectId(String(r.data[0].id));
    }).catch(() => {});
  }, []);

  const loadProject = useCallback(async (id) => {
    setLoading(true);
    try {
      const [ctx, map, assets, strat] = await Promise.all([
        axios.get(`${API}/contentiq/projects/${id}/context`).then(r => r.data).catch(() => null),
        axios.get(`${API}/contentiq/projects/${id}/message-map`).then(r => r.data).catch(() => []),
        axios.get(`${API}/contentiq/projects/${id}/sources`).then(r => r.data).catch(() => []),
        axios.get(`${API}/contentiq/projects/${id}/strategy`).then(r => r.data).catch(() => null),
      ]);
      const pj = projects.find(p => String(p.id) === String(id));
      setData({ context: ctx, mapRows: map, assets, strategy: strat, stages: pj?.stages || [] });
    } finally {
      setLoading(false);
    }
  }, [projects]);

  useEffect(() => { if (projectId) loadProject(projectId); }, [projectId, loadProject]);

  if (!def) return null;  // the effect above is already redirecting Home
  const hue = HUES[def.hueKey];
  const prereqs = data ? computePrereqs(def, data) : null;
  const ready = prereqs && !prereqs.firstMissing;
  const targetSection = ready
    ? def.wizardSection
    : (prereqs ? NEED_SECTION[prereqs.firstMissing.key] : 'context');

  // Picking up a project from a lane card makes that the project's lane
  // (migration 0131) — the wizard reads the lane from the project, so it
  // survives reopening from Home or All projects.
  const continueExisting = async () => {
    const pj = projects.find(p => String(p.id) === String(projectId));
    if (pj && pj.output_goal !== output) {
      await axios.put(`${API}/contentiq/projects/${projectId}`, { output_goal: output }).catch(() => {});
    }
    navigate(`/contentiq/project/${projectId}?goal=${output}&section=${targetSection}`);
  };

  return (
    <div style={pageShell}>
      <div style={{
        maxWidth: '660px', margin: '0 auto', padding: '36px 32px',
        boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '22px',
      }}>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button onClick={() => navigate('/contentiq')}
            style={{ ...buttonLink, fontSize: '13px', alignSelf: 'flex-start' }}>
            ← ContentIQ Home
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: RADII.tile, background: hue.main,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: NEUTRAL.pageBg, fontFamily: FONTS.display, fontWeight: 700, fontSize: '20px',
            }}>
              {CARDS.find(c => c.output === output)?.icon || def.title.slice(0, 1)}
            </div>
            <div style={{ fontFamily: FONTS.display, fontSize: '27px', fontWeight: 700 }}>{def.title}</div>
          </div>
          <div style={{ fontSize: '13px', color: INK.secondary, lineHeight: 1.55 }}>{def.blurb}</div>
        </div>

        {mode === 'new' && (
          <>
            <div style={{
              background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.border}`,
              borderRadius: RADII.card, padding: '20px 24px',
              display: 'flex', flexDirection: 'column', gap: '10px',
            }}>
              <div style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: '16px' }}>
                Start a new project
              </div>
              <div style={{ fontSize: '13px', color: INK.secondary, lineHeight: 1.55 }}>
                A short guided setup: pick the program and component, confirm the DemandArc
                product, {def.setupBlurb}
              </div>
              <button onClick={() => navigate(`/contentiq/new?goal=${output}`)}
                style={{ ...buttonPrimary(hue.main), alignSelf: 'flex-start' }}>
                Start the guided setup →
              </button>
            </div>
            {projects.length > 0 && (
              <div style={{ fontSize: '13px', color: INK.secondary }}>
                Already working on this?{' '}
                <button onClick={() => setMode('existing')} style={{ ...buttonLink, fontSize: '13px' }}>
                  Pick up an existing project
                </button>
              </div>
            )}
          </>
        )}

        {mode === 'existing' && (
        <>
        <div style={{ fontSize: '13px', color: INK.secondary }}>
          <button onClick={() => setMode('new')} style={{ ...buttonLink, fontSize: '13px' }}>
            ← Start a new project instead
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: INK.body }}>Content project</div>
          {projects.length === 0 ? (
            <div style={{ fontSize: '13px', color: INK.muted }}>
              No content projects yet —{' '}
              <button onClick={() => setMode('new')} style={{ ...buttonLink, fontSize: '13px' }}>start one</button>.
            </div>
          ) : (
            <select value={projectId} onChange={e => setProjectId(e.target.value)}
              style={{
                fontSize: '14px', padding: '10px 14px', border: `1.5px solid ${NEUTRAL.control}`,
                borderRadius: RADII.control, background: NEUTRAL.pageBg, color: INK.primary,
                maxWidth: '430px', fontFamily: FONTS.body,
              }}>
              {projects.map(pj => (
                <option key={pj.id} value={pj.id}>
                  {pj.name}{(pj.attachments || [])[0]?.client_name ? ` — ${pj.attachments[0].client_name}` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {loading && <div style={{ fontSize: '13px', color: INK.muted }}>Checking what’s in place…</div>}

        {prereqs && !loading && (
          <>
            <PrereqChecklist heading={`What ${def.noun} needs`} items={prereqs.items} />

            <div style={{
              background: HUES.strategy.tint, border: `1px solid ${HUES.strategy.border}`,
              borderRadius: RADII.card, padding: '16px 20px',
              display: 'flex', alignItems: 'center', gap: '14px',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexGrow: 1 }}>
                <div style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: '14.5px', color: HUES.strategy.deep }}>
                  {ready ? 'Everything’s in place'
                    : prereqs.firstMissing.state === 'warn' ? 'One step away'
                    : 'The wizard will take you there'}
                </div>
                <div style={{ fontSize: '12.5px', color: BRAND.link }}>
                  {ready
                    ? 'This deliverable is ready to generate.'
                    : `Next up: ${prereqs.firstMissing.label.toLowerCase()} — the wizard starts at that step.`}
                </div>
              </div>
              <button onClick={continueExisting} style={buttonPrimary()}>
                {ready ? def.readyLabel : `Continue to ${NEED_STEP_NAME[prereqs.firstMissing.key]} →`}
              </button>
            </div>

          </>
        )}
        </>
        )}

      </div>
    </div>
  );
}
